import type { UploadScope } from './runtime'

export const ABSOLUTE_MAX_BYTES = 30 * 1024 * 1024
export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024
export const IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif'

/**
 * Upload sizing per scope.
 *
 * Every card crops with object-fit: cover, so the edge that limits sharpness
 * is the short one. Sizing by the long edge (the old rule) left a tall phone
 * photo 591px wide and shrank kitchen photos — which upload as `profile` and
 * fill 4:3 kitchen cards — to 288×512. `shortEdge` is the target, `longEdge`
 * caps very tall or wide shots, and `minShortEdge` is where the soft advisory
 * starts saying the photo may look soft.
 */
export const IMAGE_PROFILES = {
  dish: { shortEdge: 1080, longEdge: 1600, budgetBytes: 420 * 1024, minShortEdge: 600 },
  menu: { shortEdge: 1080, longEdge: 1600, budgetBytes: 420 * 1024, minShortEdge: 600 },
  chef: { shortEdge: 960, longEdge: 1440, budgetBytes: 320 * 1024, minShortEdge: 480 },
  profile: { shortEdge: 768, longEdge: 1280, budgetBytes: 240 * 1024, minShortEdge: 400 },
} satisfies Record<UploadScope, { shortEdge: number; longEdge: number; budgetBytes: number; minShortEdge: number }>

/** Advice about a picked photo. Never blocks the upload. */
export interface ImageAdvisory {
  code: 'flat' | 'dark' | 'blurry' | 'small' | 'tall' | 'wide'
  message: string
}

/** Luminance summary of a downscaled copy (long edge ≤ 256px). */
export interface ImageStats {
  /** Mean luma, 0–255. */
  mean: number
  /** Luma standard deviation. */
  spread: number
  /** Variance of the 4-neighbour Laplacian: edge detail, low when out of focus. */
  detail: number
}

/*
 * Thresholds calibrated on the live uploads (2026-11-03). Across 63 photos the
 * real food shots sat at mean ≥ 43, spread ≥ 25 and detail ≥ 150; the three
 * failures — a black frame, a flat colour square, a blurred backdrop — sat at
 * mean 8, spread 2 and detail ≤ 2. The ratio limits catch the phone
 * screenshots (≈0.46) and banners (≥ 2.1) that cards crop hardest.
 */
const DARK_MEAN = 25
const FLAT_SPREAD = 12
const BLURRY_DETAIL = 60
const TALL_RATIO = 0.6
const WIDE_RATIO = 2

/** Summarise RGBA pixels for {@link adviseImage}. */
export const imageStats = (rgba: ArrayLike<number>, width: number, height: number): ImageStats => {
  const n = width * height
  const luma = new Float32Array(n)
  let sum = 0
  for (let i = 0; i < n; i++) {
    const v = 0.299 * rgba[i * 4] + 0.587 * rgba[i * 4 + 1] + 0.114 * rgba[i * 4 + 2]
    luma[i] = v
    sum += v
  }
  const mean = n ? sum / n : 0
  let squares = 0
  for (let i = 0; i < n; i++) squares += (luma[i] - mean) ** 2
  let lapSum = 0
  let lapSquares = 0
  let count = 0
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x
      const v = 4 * luma[i] - luma[i - 1] - luma[i + 1] - luma[i - width] - luma[i + width]
      lapSum += v
      lapSquares += v * v
      count++
    }
  }
  const lapMean = count ? lapSum / count : 0
  return {
    mean,
    spread: n ? Math.sqrt(squares / n) : 0,
    detail: count ? lapSquares / count - lapMean * lapMean : 0,
  }
}

/**
 * Soft guidance for one picked photo, most important first, at most two.
 * Dimensions are the original's, before upload resizing.
 */
export const adviseImage = (
  scope: UploadScope,
  size: { width: number; height: number },
  stats?: ImageStats,
): ImageAdvisory[] => {
  const advice: ImageAdvisory[] = []
  if (stats && stats.spread < FLAT_SPREAD) {
    advice.push({ code: 'flat', message: 'This photo looks almost blank. Is it the right one?' })
  } else if (stats && stats.mean < DARK_MEAN) {
    advice.push({ code: 'dark', message: 'This photo is quite dark. One taken in daylight or near a window will look better.' })
  } else if (stats && stats.detail < BLURRY_DETAIL) {
    advice.push({ code: 'blurry', message: 'This photo looks blurry. Tap your screen where you want it sharp, then take it again.' })
  }
  const short = Math.min(size.width, size.height)
  const ratio = size.width / Math.max(1, size.height)
  if (short < IMAGE_PROFILES[scope].minShortEdge) {
    advice.push({ code: 'small', message: 'This photo is a bit small, so it may look blurry on bigger screens. A larger photo will look sharper.' })
  }
  // prepareImage trims these shapes, so the advice says what already happened
  // rather than warning about a crop the reader cannot picture.
  if (ratio < TALL_RATIO) {
    advice.push({ code: 'tall', message: 'Your photo was very tall, so we cut a little off the top and bottom to make it fit. If something is missing, try a square photo.' })
  } else if (ratio > WIDE_RATIO) {
    advice.push({ code: 'wide', message: 'Your photo was very wide, so we cut a little off the sides to make it fit. If something is missing, try a square photo.' })
  }
  return advice.slice(0, 2)
}

/** The shapes trimmed to: a phone photo held upright, and a wide TV-shaped shot. */
const TRIM_TALL = 3 / 4
const TRIM_WIDE = 16 / 9

/**
 * The part of the original that gets uploaded.
 *
 * A screenshot-tall or banner-wide photo used to upload whole and leave every
 * card and avatar to crop it on its own, each differently, and the advice could
 * only warn that "the top and bottom will be cut off". Trimming once here means
 * the stored photo is the photo every slot shows. Avatars keep the upper part,
 * where a face sits in an upright selfie; food stays centred.
 */
export const uploadCrop = (scope: UploadScope, width: number, height: number) => {
  const ratio = width / Math.max(1, height)
  if (ratio < TALL_RATIO) {
    const sh = Math.round(width / TRIM_TALL)
    const bias = scope === 'profile' || scope === 'chef' ? 0.25 : 0.5
    return { sx: 0, sy: Math.round((height - sh) * bias), sw: width, sh }
  }
  if (ratio > WIDE_RATIO) {
    const sw = Math.round(height * TRIM_WIDE)
    return { sx: Math.round((width - sw) / 2), sy: 0, sw, sh: height }
  }
  return { sx: 0, sy: 0, sw: width, sh: height }
}

const STATS_EDGE = 256

const statsOf = (bitmap: ImageBitmap): ImageStats | undefined => {
  try {
    const scale = Math.min(1, STATS_EDGE / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bitmap.width * scale))
    canvas.height = Math.max(1, Math.round(bitmap.height * scale))
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx || typeof ctx.getImageData !== 'function') return undefined
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    return imageStats(ctx.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height)
  } catch {
    // Pixel reads are advice only; a browser that refuses them still uploads.
    return undefined
  }
}

export interface PreparedImage {
  file: File
  advisories: ImageAdvisory[]
}

const originalOrReject = (file: File): File => {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error('Image could not be optimized. Please choose an image under 8 MB.')
  }
  return file
}

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Image encoding timed out.')), 10_000)
    try {
      canvas.toBlob((blob) => { clearTimeout(timer); resolve(blob) }, type, quality)
    } catch (error) { clearTimeout(timer); reject(error) }
  })

/** Preserve orientation; trim extreme shapes (see uploadCrop). Bound quality attempts and dimension reductions. */
export const prepareImageForUpload = async (file: File, scope: UploadScope): Promise<File> =>
  (await prepareImage(file, scope)).file

/** {@link prepareImageForUpload}, plus soft advice about the original photo. */
export const prepareImage = async (file: File, scope: UploadScope): Promise<PreparedImage> => {
  const plain = (kept: File): PreparedImage => ({ file: kept, advisories: [] })
  if (!file.size || !IMAGE_ACCEPT.split(',').includes(file.type)) {
    throw new Error('Please choose a PNG, JPEG, WebP, or GIF image.')
  }
  if (file.size > ABSOLUTE_MAX_BYTES) {
    throw new Error('Image is too large — please pick one under 30 MB.')
  }
  if (typeof document === 'undefined' || file.type === 'image/gif') return plain(originalOrReject(file))
  if (file.type === 'image/webp') {
    try {
      const header = new TextDecoder().decode(await file.slice(0, 128).arrayBuffer())
      if (header.includes('ANIM')) return plain(originalOrReject(file))
    } catch { return plain(originalOrReject(file)) }
  }
  let bitmap: ImageBitmap
  try { bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' }) }
  catch { return plain(originalOrReject(file)) }
  try {
    const profile = IMAGE_PROFILES[scope]
    const advisories = adviseImage(scope, bitmap, statsOf(bitmap))
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return { file: originalOrReject(file), advisories }
    const crop = uploadCrop(scope, bitmap.width, bitmap.height)
    const draw = () => ctx.drawImage(bitmap, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, canvas.width, canvas.height)
    const short = Math.min(crop.sw, crop.sh)
    const long = Math.max(crop.sw, crop.sh)
    let reduce = 1
    for (let attempt = 0; attempt < 5; attempt++) {
      const scale = Math.min(1, (profile.shortEdge * reduce) / short, (profile.longEdge * reduce) / long)
      canvas.width = Math.max(1, Math.round(crop.sw * scale))
      canvas.height = Math.max(1, Math.round(crop.sh * scale))
      draw()
      for (const quality of [0.82, 0.74, 0.66, 0.6]) {
        let blob = await canvasToBlob(canvas, 'image/webp', quality)
        if (blob?.type !== 'image/webp') {
          // A white background preserves transparent sources in the JPEG fallback.
          ctx.fillStyle = '#fff'
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          draw()
          blob = await canvasToBlob(canvas, 'image/jpeg', quality)
        }
        if (!blob || !['image/webp', 'image/jpeg'].includes(blob.type)) continue
        if (blob.size <= profile.budgetBytes) {
          const base = file.name.replace(/\.[^./\\]+$/, '') || 'image'
          return { file: new File([blob], `${base}.${blob.type === 'image/webp' ? 'webp' : 'jpg'}`, { type: blob.type }), advisories }
        }
      }
      reduce *= 0.75
    }
    throw new Error('Image could not fit the upload budget. Please choose a simpler image.')
  } finally { bitmap.close() }
}

/** Verification photos stay sharp enough for a reviewer to read an ID. */
const DOCUMENT_MAX_EDGE = 2400
const DOCUMENT_RESIZE_ABOVE_BYTES = 3 * 1024 * 1024

/**
 * Shrink a large verification photo before it leaves the phone.
 *
 * A modern phone photo is 4–10 MB, which is a slow upload on mobile data and
 * close to the server's 10 MB cap. Downscaling to 2400px at high JPEG quality
 * keeps an ID card's text legible while cutting most of the bytes. Small
 * photos, PDFs and anything the browser cannot decode are sent untouched, so
 * this can only ever make an upload smaller.
 */
export const prepareDocumentPhoto = async (file: File): Promise<File> => {
  if (typeof document === 'undefined' || file.size <= DOCUMENT_RESIZE_ABOVE_BYTES) return file
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) return file
  let bitmap: ImageBitmap
  try { bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' }) } catch { return file }
  try {
    const scale = Math.min(1, DOCUMENT_MAX_EDGE / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bitmap.width * scale))
    canvas.height = Math.max(1, Math.round(bitmap.height * scale))
    const ctx = canvas.getContext('2d')
    if (!ctx) return file
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    const blob = await canvasToBlob(canvas, 'image/jpeg', 0.9).catch(() => null)
    if (!blob || blob.type !== 'image/jpeg' || blob.size >= file.size) return file
    const base = file.name.replace(/\.[^./\\]+$/, '') || 'photo'
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg' })
  } finally { bitmap.close() }
}
